import { useEffect, useRef, useState } from 'react';

import { EMAIL_RESEND_COOLDOWN_SECONDS, isMfaSessionEnded } from '../../../../shared/MfaManager/consts';
import { useAddToast } from '../../../contexts/ToastContext';
import { useResendCooldown } from '../../../hooks/useResendCooldown';
import { getErrorMessage } from '../../../ReactQuery/errors';
import { hardRedirectTo } from '../../../utils';
import Button from '../../generic/Button/Button';
import AlphanumericCodeInput, { type AlphanumericCodeInputHandle } from '../../generic/Forms/AlphanumericCodeInput';
import VerificationCodeInput, { type VerificationCodeInputHandle } from '../../generic/Forms/VerificationCodeInput';
import UseDifferentAccountButton from '../components/UseDifferentAccountButton';
import { presentRequestError } from '../errorPresentation';
import { useResendEmailMfa } from '../hooks/useResendEmailMfa';
import { useVerifyMfa } from '../hooks/useVerifyMfa';
import type { MfaMethodType } from '../../../../shared/model-typings/mfaMethod';

import styles from '../Login.module.less';

export type MfaStepProps = {
  mfaMethod: MfaMethodType;
  /**
   * Post-login redirect target, propagated from the URL `?next=` so a redirect-initiated login still lands the user
   *  back at their original destination once MFA completes.
   */
  next?: string;
  /** Returns to the identifier step — for the manual "use a different account" link and when the MFA session dies. */
  onBack: () => void;
};

/**
 * The MFA challenge: after a credential/social login lands in the partially-authenticated state, the user enters the
 * code from their authenticator app (TOTP) or email to finish signing in. TOTP offers a backup-code fallback; email
 * offers a resend. On success the full session is established server-side and we hard-redirect into the app.
 */
const MfaStep = ({ mfaMethod, next, onBack }: MfaStepProps) => {
  const [code, setCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Started on mount for email: reaching this step means the login MFA gate already mailed a code, so the
  // server's resend window is running. Only the email challenge has a resend control, so a TOTP challenge
  // starts idle and runs no countdown.
  const resendCooldown = useResendCooldown(EMAIL_RESEND_COOLDOWN_SECONDS, {
    startOnMount: mfaMethod === 'email',
  });
  const verifyMfa = useVerifyMfa();
  const resendEmailMfa = useResendEmailMfa();
  const addToast = useAddToast();
  const codeInputRef = useRef<VerificationCodeInputHandle | AlphanumericCodeInputHandle | null>(null);
  // The settle effect re-runs whenever the parent re-renders (its inline callbacks get new identities) while the
  // mutation result stays latched, so guard it to fire once per settled submission (mirrors VerifyEmailStep).
  const hasHandledResultRef = useRef(false);
  // Same guard for the resend failure below. Without it the effect re-fires on every render that changes its
  // deps while `isError` stays latched — and since toasting re-renders every `useAddToast` consumer, a caller
  // passing an unmemoized `onBack` turns one failed resend into an unbounded toast loop.
  const hasHandledResendErrorRef = useRef(false);

  // React to the verification result from an effect rather than the mutate callbacks. React Query runs those callbacks
  // before notifying its subscribers, so navigating away there — on success, or when a dead session forces a restart —
  // unmounts this step while React Query is still about to update it, warning about a state update on an unmounted
  // component under React 17. Reacting to the settled result defers any navigation until after this step re-renders.
  useEffect(() => {
    if (hasHandledResultRef.current) {
      return;
    }
    if (verifyMfa.isSuccess) {
      hasHandledResultRef.current = true;
      // The URL `?next=` (credential path) wins; the server-resolved value covers the SSO/social path, whose
      // IdP round-trip returns to a bare `/auth/login` with the deep link stashed in the session instead.
      hardRedirectTo(next ?? verifyMfa.data?.next ?? '/');
      return;
    }
    if (!verifyMfa.isError) {
      return;
    }
    hasHandledResultRef.current = true;
    // The verify endpoint rejects with 401/429 for both wrong codes and dead sessions. A wrong code is shown inline so
    // the user can retry; a dead session can't be retried, so toast why and restart the flow. Anything unexpected
    // (network failure, 5xx) is a toast.
    const presented = presentRequestError(verifyMfa.error, {
      inlineFallback: 'Invalid code. Please try again.',
      toastFallback: 'Something went wrong. Please try again.',
    });
    if (presented.kind === 'toast') {
      addToast({ type: 'error', content: presented.message });
      return;
    }
    if (isMfaSessionEnded(presented.message)) {
      addToast({ type: 'error', content: presented.message });
      onBack();
      return;
    }
    setCode('');
    setError(presented.message);
    // Send the cursor back to the first box — without this it stays on whichever box the user last typed in.
    codeInputRef.current?.focusFirst();
  }, [verifyMfa.isSuccess, verifyMfa.isError, verifyMfa.error, verifyMfa.data, next, onBack, addToast]);

  // Resend errors are surfaced by the hook now; every failure toasts, and a dead pending-MFA session additionally
  // restarts the flow instead of leaving the user stuck on a challenge they can no longer complete.
  useEffect(() => {
    if (hasHandledResendErrorRef.current || !resendEmailMfa.isError) {
      return;
    }
    hasHandledResendErrorRef.current = true;
    const message = getErrorMessage(resendEmailMfa.error, 'Could not resend the code. Please try again.');
    addToast({ type: 'error', content: message });
    if (isMfaSessionEnded(message)) {
      onBack();
    }
  }, [resendEmailMfa.isError, resendEmailMfa.error, onBack, addToast]);

  const handleSubmit = (submittedCode: string = code) => {
    if (!submittedCode || verifyMfa.isLoading) {
      return;
    }
    hasHandledResultRef.current = false;
    setError(null);
    verifyMfa.mutate({ code: submittedCode, isBackupCode: useBackupCode });
  };

  const handleToggleBackupCode = () => {
    setUseBackupCode((previous) => !previous);
    setCode('');
    setError(null);
  };

  const handleResend = () => {
    if (resendCooldown.isCoolingDown || resendEmailMfa.isLoading) {
      return;
    }
    hasHandledResendErrorRef.current = false;
    resendEmailMfa.mutate(undefined, {
      onSuccess: resendCooldown.start,
    });
  };

  let instructions = 'Enter the 6-digit verification code from your authenticator app.';
  if (mfaMethod === 'email') {
    instructions = 'Enter the 6-digit verification code sent to your email.';
  } else if (useBackupCode) {
    instructions = 'Enter one of your backup codes.';
  }

  return (
    <div className={styles.card}>
      <h1 className={styles.heading}>Verify your identity</h1>
      <p className={styles.placeholder}>{instructions}</p>
      <div className={styles.codeField} data-test-id="mfa-code-field">
        {useBackupCode ? (
          <AlphanumericCodeInput
            ref={codeInputRef}
            value={code}
            onChange={setCode}
            onComplete={handleSubmit}
            disabled={verifyMfa.isLoading}
          />
        ) : (
          <VerificationCodeInput
            ref={codeInputRef}
            value={code}
            onChange={setCode}
            onComplete={handleSubmit}
            disabled={verifyMfa.isLoading}
          />
        )}
      </div>
      {error && (
        <p role="alert" className={styles.error}>
          {error}
        </p>
      )}
      <Button isFullWidth size="large" isDisabled={verifyMfa.isLoading} onClick={() => handleSubmit()}>
        Verify
      </Button>
      {mfaMethod === 'email' ? (
        <Button
          type="anchor"
          size="small"
          className={styles.secondaryAction}
          isDisabled={resendCooldown.isCoolingDown || resendEmailMfa.isLoading}
          onClick={handleResend}
        >
          {resendCooldown.isCoolingDown ? `Resend code (${resendCooldown.secondsRemaining}s)` : 'Resend code'}
        </Button>
      ) : (
        <Button type="anchor" size="small" className={styles.secondaryAction} onClick={handleToggleBackupCode}>
          {useBackupCode ? 'Use authenticator app code' : 'Having trouble? Use a backup code'}
        </Button>
      )}
      <UseDifferentAccountButton onClick={onBack} />
    </div>
  );
};

export default MfaStep;
